import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, query, orderBy, onSnapshot, getDocs, doc, getDoc, writeBatch, where, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { font } from '../Orders/Roboto-Regular-font';

const AllShipments = () => {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [products, setProducts] = useState([]);
    const { currentUser, userData } = useAuth();
    const navigate = useNavigate();

    // Detail modal state
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedItems, setEditedItems] = useState([]);
    const [editedCargoCompany, setEditedCargoCompany] = useState('');
    const [editedTrackingNumber, setEditedTrackingNumber] = useState('');
    const [editedVatRate, setEditedVatRate] = useState(0);
    const [newItemSearch, setNewItemSearch] = useState('');
    const [newItemProductId, setNewItemProductId] = useState('');
    const [newItemQuantity, setNewItemQuantity] = useState(1);
    const [newItemPrice, setNewItemPrice] = useState(0);
    const [newItemName, setNewItemName] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const styles = {
        container: {
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            background: 'transparent',
            padding: '24px'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
        },
        title: {
            fontSize: '32px',
            fontWeight: '700',
            color: '#0f172a',
            margin: 0,
            letterSpacing: '-0.5px'
        },
        box: {
            background: 'white',
            padding: '32px',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '32px',
            border: '1px solid #e2e8f0'
        },
        table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: 'white', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' },
        th: { background: '#f8fafc', padding: '16px 20px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '12px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
        td: { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#374151' },
        input: { padding: '12px 16px', fontSize: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', width: '100%', background: '#fff', outline: 'none' },
        searchInputContainer: {
            position: 'relative',
            width: '100%'
        },
        searchIcon: {
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94a3b8',
            width: '16px',
            height: '16px'
        },
        actionBtn: {
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontWeight: '600',
            fontSize: '12px',
            cursor: 'pointer',
            background: 'white',
            color: '#06C167',
            transition: 'all 0.2s',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        },
        statusBadge: {
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase'
        },
        // Modal styles
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
        },
        modal: {
            background: 'white',
            borderRadius: '20px',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            position: 'relative'
        },
        modalHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 32px',
            borderBottom: '1px solid #e2e8f0',
            background: '#f8fafc',
            borderRadius: '20px 20px 0 0'
        },
        modalBody: {
            padding: '32px'
        },
        modalTitle: {
            fontSize: '20px',
            fontWeight: '700',
            color: '#0f172a',
            margin: 0
        },
        closeBtn: {
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#64748b',
            padding: '4px 8px',
            borderRadius: '8px',
            transition: 'background 0.2s'
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '28px'
        },
        infoCard: {
            background: '#f8fafc',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
        },
        infoLabel: {
            fontSize: '11px',
            fontWeight: '600',
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
        },
        infoValue: {
            fontSize: '15px',
            fontWeight: '600',
            color: '#0f172a'
        },
        editInput: {
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontSize: '14px',
            color: '#0f172a',
            width: '100%',
            boxSizing: 'border-box',
            outline: 'none',
            transition: 'border-color 0.2s',
            background: 'white'
        },
        editInputSmall: {
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            fontSize: '13px',
            color: '#0f172a',
            width: '80px',
            textAlign: 'center',
            outline: 'none',
            background: 'white'
        },
        btnPrimary: {
            padding: '10px 20px',
            borderRadius: '10px',
            border: 'none',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            background: '#06C167',
            color: 'white',
            transition: 'all 0.2s',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        },
        btnDanger: {
            padding: '10px 20px',
            borderRadius: '10px',
            border: 'none',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            background: '#ef4444',
            color: 'white',
            transition: 'all 0.2s',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        },
        btnSecondary: {
            padding: '10px 20px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            background: 'white',
            color: '#374151',
            transition: 'all 0.2s',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        },
        btnSuccess: {
            padding: '10px 20px',
            borderRadius: '10px',
            border: 'none',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            background: '#10b981',
            color: 'white',
            transition: 'all 0.2s',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        },
        totalRow: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '20px 0',
            borderTop: '2px solid #e2e8f0',
            marginTop: '8px'
        },
        totalBox: {
            background: 'linear-gradient(135deg, #06C167 0%, #8b5cf6 100%)',
            color: 'white',
            padding: '16px 28px',
            borderRadius: '12px',
            fontSize: '18px',
            fontWeight: '700'
        },
        clickableRow: {
            cursor: 'pointer',
            transition: 'background 0.15s'
        }
    };

    const safeString = (value, fallback = '-') => (value === null || typeof value === 'undefined' || value === '') ? fallback : String(value).trim();
    const safeNumber = (value, fallback = 0) => (typeof value !== 'number' || isNaN(value)) ? fallback : value;

    useEffect(() => {
        if (!userData?.companyId) return;

        const fetchInitialData = async () => {
            try {
                const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
                if (companyDoc.exists()) setCompanyInfo(companyDoc.data());

                const productsSnapshot = await getDocs(collection(db, 'companies', userData.companyId, 'products'));
                setProducts(productsSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (error) {
                console.error("Data fetching error:", error);
            }
        };

        fetchInitialData();

        const ordersQuery = query(
            collection(db, 'companies', userData.companyId, 'orders'),
            orderBy('orderDate', 'desc')
        );

        const unsubscribe = onSnapshot(ordersQuery, async (snapshot) => {
            try {
                const shipmentPromises = snapshot.docs.map(async (orderDoc) => {
                    const orderData = { id: orderDoc.id, ...orderDoc.data() };
                    try {
                        const shipmentsSnapshot = await getDocs(collection(db, 'companies', userData.companyId, 'orders', orderDoc.id, 'shipments'));
                        return shipmentsSnapshot.docs.map(shipmentDoc => ({
                            id: shipmentDoc.id,
                            ...shipmentDoc.data(),
                            orderId: orderDoc.id,
                            orderNumber: orderData.orderNumber || orderDoc.id.substring(0, 8),
                            customerName: orderData.customerName,
                            customerId: orderData.customerId,
                            order: orderData
                        }));
                    } catch (error) {
                        console.error('Error fetching shipments for order:', orderDoc.id, error);
                        return [];
                    }
                });

                const results = await Promise.all(shipmentPromises);
                const allShipments = results.flat();

                allShipments.sort((a, b) => {
                    const getTime = (d) => {
                        if (!d) return 0;
                        // Firestore Timestamp
                        if (typeof d.seconds === 'number') return d.seconds;
                        // JS Date object
                        if (d instanceof Date) return d.getTime() / 1000;
                        // String date
                        if (typeof d === 'string') {
                            const parsed = new Date(d).getTime();
                            return isNaN(parsed) ? 0 : parsed / 1000;
                        }
                        return 0;
                    };
                    const dateA = getTime(a.shipmentDate);
                    const dateB = getTime(b.shipmentDate);
                    return dateB - dateA; // Descending (newest first)
                });

                setShipments(allShipments);
                setLoading(false);
            } catch (error) {
                console.error("Snapshot processing error:", error);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [userData]);

    const generateShipmentPDF = async (shipment) => {
        try {
            const order = shipment.order;
            const pdfDoc = new jsPDF();
            pdfDoc.addFileToVFS('Roboto-Regular.ttf', font);
            pdfDoc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
            pdfDoc.setFont('Roboto');

            const getProduct = (productId) => products.find(p => p.id === productId);

            pdfDoc.setFontSize(22);
            pdfDoc.setTextColor(73, 80, 87);
            pdfDoc.text(safeString(companyInfo?.companyName, "Firma Adı"), 14, 22);
            pdfDoc.setFontSize(18);
            pdfDoc.setTextColor(149, 165, 166);
            pdfDoc.text("SEVK İRSALİYESİ", 200, 22, { align: 'right' });
            pdfDoc.line(14, 28, 200, 28);

            pdfDoc.setFontSize(10);
            pdfDoc.text("Firma Bilgileri", 14, 40);
            pdfDoc.setFontSize(9);
            pdfDoc.text(safeString(companyInfo?.address), 14, 46);
            pdfDoc.text(`Tel: ${safeString(companyInfo?.phone)}`, 14, 52);
            pdfDoc.text(`VD: ${safeString(companyInfo?.taxOffice)} / ${safeString(companyInfo?.taxNumber)}`, 14, 58);

            pdfDoc.setFontSize(10);
            pdfDoc.text("Müşteri Bilgileri", 120, 40);
            pdfDoc.setFontSize(9);
            pdfDoc.text(safeString(order.customerName), 120, 46);
            if (order.customerAddress) pdfDoc.text(safeString(order.customerAddress), 120, 52);
            if (order.customerPhone) pdfDoc.text(`Tel: ${safeString(order.customerPhone)}`, 120, 58);

            pdfDoc.setFontSize(10);
            pdfDoc.text("İrsaliye No:", 200, 40, { align: 'right' });
            pdfDoc.setFontSize(9);
            pdfDoc.text(shipment.id.substring(0, 8).toUpperCase(), 200, 46, { align: 'right' });
            pdfDoc.text("Tarih:", 200, 52, { align: 'right' });
            const shipmentDate = shipment.shipmentDate?.seconds ? new Date(shipment.shipmentDate.seconds * 1000).toLocaleDateString() : 'Tarih Yok';
            pdfDoc.text(shipmentDate, 200, 58, { align: 'right' });

            const tableColumn = ["Barkod", "Ürün Adı", "Miktar", "Birim Fiyat", "Toplam Tutar"];
            const tableRows = (shipment.items || []).map(item => {
                const product = getProduct(item.productId);
                const itemTotal = safeNumber(item.price) * safeNumber(item.quantity);
                return [
                    product?.barcode || '-',
                    safeString(item.name),
                    safeString(item.quantity),
                    `${safeNumber(item.price).toFixed(2)} ${order.currency === 'USD' ? '$' : '₺'}`,
                    `${itemTotal.toFixed(2)} ${order.currency === 'USD' ? '$' : '₺'}`,
                ];
            });

            autoTable(pdfDoc, {
                head: [tableColumn],
                body: tableRows,
                startY: 75,
                theme: 'striped',
                headStyles: { fillColor: [44, 62, 80] },
                styles: { font: 'Roboto', fontStyle: 'normal' },
            });

            const finalY = pdfDoc.lastAutoTable.finalY;
            pdfDoc.setFontSize(10);
            pdfDoc.text(`Kargo: ${safeString(shipment.cargoCompany)}`, 14, finalY + 15);
            pdfDoc.text(`Takip No: ${safeString(shipment.trackingNumber)}`, 14, finalY + 21);

            const totalAmount = (shipment.items || []).reduce((acc, item) => acc + (safeNumber(item.price) * safeNumber(item.quantity)), 0);
            pdfDoc.text(`Toplam: ${totalAmount.toFixed(2)} ${order.currency === 'USD' ? '$' : '₺'}`, 200, finalY + 15, { align: 'right' });

            pdfDoc.save(`${order.customerName}-Irsaliye-${shipment.id.substring(0, 6)}.pdf`);
        } catch (error) {
            console.error("PDF Error:", error);
            alert("PDF oluşturulurken hata oluştu.");
        }
    };

    // Open detail modal
    const handleOpenDetail = (shipment) => {
        setSelectedShipment(shipment);
        setIsEditing(false);
        setEditedItems((shipment.items || []).map(item => ({ ...item })));
        setEditedCargoCompany(shipment.cargoCompany || '');
        setEditedTrackingNumber(shipment.trackingNumber || '');
        setEditedVatRate(shipment.order?.vatRate || 0);
        resetNewItem();
    };

    const handleCloseModal = () => {
        setSelectedShipment(null);
        setIsEditing(false);
    };

    const handleStartEdit = () => {
        setIsEditing(true);
        setEditedItems((selectedShipment.items || []).map(item => ({ ...item })));
        setEditedCargoCompany(selectedShipment.cargoCompany || '');
        setEditedTrackingNumber(selectedShipment.trackingNumber || '');
        setEditedVatRate(selectedShipment.order?.vatRate || 0);
        resetNewItem();
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedItems((selectedShipment.items || []).map(item => ({ ...item })));
        setEditedCargoCompany(selectedShipment.cargoCompany || '');
        setEditedTrackingNumber(selectedShipment.trackingNumber || '');
        setEditedVatRate(selectedShipment.order?.vatRate || 0);
        resetNewItem();
    };

    const handleEditItemChange = (index, field, value) => {
        setEditedItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: Number(value) };
            return updated;
        });
    };

    const handleRemoveEditItem = (index) => {
        setEditedItems(prev => prev.filter((_, i) => i !== index));
    };

    const resetNewItem = () => {
        setNewItemSearch('');
        setNewItemProductId('');
        setNewItemQuantity(1);
        setNewItemPrice(0);
        setNewItemName('');
    };

    const handleNewItemProductSelect = (product) => {
        setNewItemProductId(product.id);
        setNewItemSearch(product.name);
        setNewItemName(product.name);
        setNewItemPrice(product.salePrice || 0);
    };

    const handleAddNewItem = () => {
        if (!newItemProductId || newItemQuantity <= 0) {
            alert('Lütfen bir ürün seçin ve miktar girin.');
            return;
        }
        if (editedItems.find(i => i.productId === newItemProductId)) {
            alert('Bu ürün zaten listede mevcut. Lütfen miktarını düzenleyin.');
            return;
        }
        setEditedItems(prev => [...prev, {
            productId: newItemProductId,
            name: newItemName,
            quantity: newItemQuantity,
            price: newItemPrice
        }]);
        resetNewItem();
    };

    const filteredNewItemProducts = newItemSearch && !newItemProductId
        ? products.filter(p =>
            p.name.toLowerCase().includes(newItemSearch.toLowerCase()) ||
            (p.barcode && p.barcode.includes(newItemSearch))
        ).slice(0, 8)
        : [];

    // Save edited shipment
    const handleSaveEdit = async () => {
        if (!selectedShipment || !userData?.companyId) return;
        setSaving(true);

        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const shipment = selectedShipment;
        const order = shipment.order;
        const originalItems = shipment.items || [];

        try {
            // 1. Update the shipment document
            const shipmentRef = doc(db, 'companies', companyId, 'orders', shipment.orderId, 'shipments', shipment.id);
            batch.update(shipmentRef, {
                items: editedItems,
                cargoCompany: editedCargoCompany,
                trackingNumber: editedTrackingNumber
            });

            // 2. Handle stock differences for edited/added items
            for (const editedItem of editedItems) {
                const originalItem = originalItems.find(oi => oi.productId === editedItem.productId);
                const oldQty = originalItem ? safeNumber(originalItem.quantity) : 0;
                const newQty = safeNumber(editedItem.quantity);
                const qtyDiff = newQty - oldQty;

                if (qtyDiff !== 0) {
                    const productRef = doc(db, 'companies', companyId, 'products', editedItem.productId);
                    const productDoc = await getDoc(productRef);

                    if (productDoc.exists()) {
                        const productData = productDoc.data();
                        const currentStock = productData.quantity || 0;
                        const newStock = currentStock - qtyDiff;
                        batch.update(productRef, { quantity: newStock });

                        const movementRef = doc(collection(productRef, 'movements'));
                        const movementType = qtyDiff > 0 ? 'Çıkış' : 'Giriş';
                        batch.set(movementRef, {
                            date: serverTimestamp(),
                            type: `İrsaliye Düzenleme - ${movementType} (İrsaliye: ${shipment.id.substring(0, 6)})`,
                            quantity: `${qtyDiff > 0 ? '-' : '+'}${Math.abs(qtyDiff)} ${productData.unit || 'Adet'}`,
                            shipmentId: shipment.id,
                            orderId: shipment.orderId
                        });
                    }
                }
            }

            // 2b. Handle removed items - restore their stock
            for (const originalItem of originalItems) {
                const stillExists = editedItems.find(ei => ei.productId === originalItem.productId);
                if (!stillExists) {
                    const productRef = doc(db, 'companies', companyId, 'products', originalItem.productId);
                    const productDoc = await getDoc(productRef);
                    if (productDoc.exists()) {
                        const productData = productDoc.data();
                        const currentStock = productData.quantity || 0;
                        const restoredQty = safeNumber(originalItem.quantity);
                        batch.update(productRef, { quantity: currentStock + restoredQty });

                        const movementRef = doc(collection(productRef, 'movements'));
                        batch.set(movementRef, {
                            date: serverTimestamp(),
                            type: `İrsaliye Düzenleme - Ürün Çıkarıldı (İrsaliye: ${shipment.id.substring(0, 6)})`,
                            quantity: `+${restoredQty} ${productData.unit || 'Adet'}`,
                            shipmentId: shipment.id,
                            orderId: shipment.orderId
                        });
                    }
                }
            }

            // 3. Handle customer balance difference (if there's an invoice)
            const oldTotal = originalItems.reduce((acc, item) => acc + (safeNumber(item.price) * safeNumber(item.quantity)), 0);
            const newTotal = editedItems.reduce((acc, item) => acc + (safeNumber(item.price) * safeNumber(item.quantity)), 0);
            const totalDiff = newTotal - oldTotal;

            if (totalDiff !== 0 && shipment.customerId) {
                const customerRef = doc(db, 'companies', companyId, 'customers', shipment.customerId);
                const customerDoc = await getDoc(customerRef);

                if (customerDoc.exists()) {
                    const customerData = customerDoc.data();
                    const currentBalance = customerData.balance || 0;

                    // Convert if needed
                    let convertedDiff = totalDiff;
                    if (customerData.currency !== order.currency) {
                        if (order.currency === 'USD' && customerData.currency === 'TRY') {
                            convertedDiff = totalDiff * (order.exchangeRate || 1);
                        } else if (order.currency === 'TRY' && customerData.currency === 'USD') {
                            convertedDiff = totalDiff / (order.exchangeRate || 1);
                        }
                    }

                    // More total = more debt (balance decreases)
                    const newBalance = currentBalance - convertedDiff;
                    batch.update(customerRef, { balance: newBalance });

                    // Add balance history
                    const balanceHistoryRef = doc(collection(customerRef, 'balanceHistory'));
                    batch.set(balanceHistoryRef, {
                        date: serverTimestamp(),
                        type: 'İrsaliye Düzenleme',
                        description: `İrsaliye #${shipment.id.substring(0, 6)} düzenlendi`,
                        amount: Math.abs(convertedDiff),
                        previousBalance: currentBalance,
                        newBalance: newBalance,
                        orderId: shipment.orderId,
                        shipmentId: shipment.id
                    });
                }

                // Update related invoice if exists
                if (shipment.invoiceId) {
                    const invoiceRef = doc(db, 'companies', companyId, 'invoices', shipment.invoiceId);
                    const invoiceDoc = await getDoc(invoiceRef);
                    if (invoiceDoc.exists()) {
                        const newSubTotal = newTotal;
                        const newVatAmount = newSubTotal * editedVatRate;
                        const newInvoiceTotal = newSubTotal + newVatAmount;
                        batch.update(invoiceRef, {
                            lineItems: editedItems,
                            subTotal: newSubTotal,
                            vatRate: editedVatRate,
                            vatAmount: newVatAmount,
                            total: newInvoiceTotal
                        });
                    }
                }

                // Update order vatRate if changed
                const orderRef = doc(db, 'companies', companyId, 'orders', shipment.orderId);
                batch.update(orderRef, { vatRate: editedVatRate });
            }

            // 4. Activity log
            const logRef = doc(collection(db, 'companies', companyId, 'activityLog'));
            batch.set(logRef, {
                userId: currentUser.uid,
                userEmail: currentUser.email,
                action: 'Sevk irsaliyesi düzenlendi',
                details: {
                    shipmentId: shipment.id,
                    orderId: shipment.orderId,
                    customerName: shipment.customerName
                },
                timestamp: serverTimestamp()
            });

            await batch.commit();

            // Update local state
            setSelectedShipment(prev => ({
                ...prev,
                items: editedItems,
                cargoCompany: editedCargoCompany,
                trackingNumber: editedTrackingNumber
            }));
            setIsEditing(false);
            alert('İrsaliye başarıyla güncellendi!');
        } catch (error) {
            console.error('İrsaliye güncelleme hatası:', error);
            alert('İrsaliye güncellenirken bir hata oluştu: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    // Delete shipment
    const handleDeleteShipment = async () => {
        if (!selectedShipment || !userData?.companyId) return;

        const confirmMsg = `Bu sevk irsaliyesini silmek istediğinizden emin misiniz?\n\n` +
            `Müşteri: ${selectedShipment.customerName}\n` +
            `Sipariş No: #${selectedShipment.orderNumber}\n\n` +
            `Bu işlem:\n` +
            `• Sevkiyat kaydını silecektir\n` +
            `• Gönderilen ürünleri stoğa geri ekleyecektir\n` +
            `• Müşteri cari bakiyesini güncelleyecektir\n` +
            `• İlgili faturayı silecektir\n\n` +
            `Bu işlem geri alınamaz!`;

        if (!window.confirm(confirmMsg)) return;

        setDeleting(true);
        const companyId = userData.companyId;
        const batch = writeBatch(db);
        const shipment = selectedShipment;
        const order = shipment.order;
        const items = shipment.items || [];

        try {
            // 1. Restore stock for each item
            for (const item of items) {
                const productRef = doc(db, 'companies', companyId, 'products', item.productId);
                const productDoc = await getDoc(productRef);

                if (productDoc.exists()) {
                    const productData = productDoc.data();
                    const currentStock = productData.quantity || 0;
                    const newStock = currentStock + safeNumber(item.quantity);
                    batch.update(productRef, { quantity: newStock });

                    // Add movement record for stock restoration
                    const movementRef = doc(collection(productRef, 'movements'));
                    batch.set(movementRef, {
                        date: serverTimestamp(),
                        type: `İrsaliye İptali - Giriş (İrsaliye: ${shipment.id.substring(0, 6)})`,
                        quantity: `+${safeNumber(item.quantity)} ${productData.unit || 'Adet'}`,
                        shipmentId: shipment.id,
                        orderId: shipment.orderId
                    });
                }
            }

            // 2. Restore customer balance (if invoice exists)
            const shipmentTotal = items.reduce((acc, item) => acc + (safeNumber(item.price) * safeNumber(item.quantity)), 0);

            if (shipment.customerId) {
                const customerRef = doc(db, 'companies', companyId, 'customers', shipment.customerId);
                const customerDoc = await getDoc(customerRef);

                if (customerDoc.exists()) {
                    const customerData = customerDoc.data();
                    const currentBalance = customerData.balance || 0;

                    // Restore balance (reverse the debt)
                    const vatRate = order.vatRate || 0;
                    const vatAmount = shipmentTotal * vatRate;
                    const invoiceTotal = shipmentTotal + vatAmount;

                    let convertedInvoiceTotal = invoiceTotal;
                    if (customerData.currency !== order.currency) {
                        if (order.currency === 'USD' && customerData.currency === 'TRY') {
                            convertedInvoiceTotal = invoiceTotal * (order.exchangeRate || 1);
                        } else if (order.currency === 'TRY' && customerData.currency === 'USD') {
                            convertedInvoiceTotal = invoiceTotal / (order.exchangeRate || 1);
                        }
                    }

                    const newBalance = currentBalance + convertedInvoiceTotal;
                    batch.update(customerRef, { balance: newBalance });

                    // Add balance history
                    const balanceHistoryRef = doc(collection(customerRef, 'balanceHistory'));
                    batch.set(balanceHistoryRef, {
                        date: serverTimestamp(),
                        type: 'İrsaliye İptali',
                        description: `İrsaliye #${shipment.id.substring(0, 6)} silindi - bakiye geri yüklendi`,
                        amount: convertedInvoiceTotal,
                        previousBalance: currentBalance,
                        newBalance: newBalance,
                        orderId: shipment.orderId,
                        shipmentId: shipment.id
                    });
                }
            }

            // 3. Delete related invoice
            if (shipment.invoiceId) {
                const invoiceRef = doc(db, 'companies', companyId, 'invoices', shipment.invoiceId);
                batch.delete(invoiceRef);
            }

            // 4. Delete related transactions
            try {
                const transactionsSnapshot = await getDocs(
                    query(
                        collection(db, 'companies', companyId, 'transactions'),
                        where('orderId', '==', shipment.orderId)
                    )
                );
                // Only delete transactions that reference this specific shipment's invoice
                transactionsSnapshot.docs.forEach(txDoc => {
                    const txData = txDoc.data();
                    if (txData.invoiceId === shipment.invoiceId || txData.shipmentId === shipment.id) {
                        batch.delete(txDoc.ref);
                    }
                });
            } catch (txError) {
                console.warn('Transaction cleanup warning:', txError);
            }

            // 5. Delete the shipment document
            const shipmentRef = doc(db, 'companies', companyId, 'orders', shipment.orderId, 'shipments', shipment.id);
            batch.delete(shipmentRef);

            // 6. Update order status
            const orderRef = doc(db, 'companies', companyId, 'orders', shipment.orderId);
            const remainingShipmentsSnap = await getDocs(collection(db, 'companies', companyId, 'orders', shipment.orderId, 'shipments'));
            const remainingCount = remainingShipmentsSnap.docs.filter(d => d.id !== shipment.id).length;

            if (remainingCount === 0) {
                batch.update(orderRef, { status: 'Bekliyor' });
            } else {
                batch.update(orderRef, { status: 'Kısmen Gönderildi' });
            }

            // 7. Activity log
            const logRef = doc(collection(db, 'companies', companyId, 'activityLog'));
            batch.set(logRef, {
                userId: currentUser.uid,
                userEmail: currentUser.email,
                action: 'Sevk irsaliyesi silindi',
                details: {
                    shipmentId: shipment.id,
                    orderId: shipment.orderId,
                    customerName: shipment.customerName,
                    itemCount: items.length,
                    totalAmount: shipmentTotal
                },
                timestamp: serverTimestamp()
            });

            await batch.commit();

            setSelectedShipment(null);
            alert('İrsaliye başarıyla silindi. Stok ve müşteri bakiyesi güncellendi.');
        } catch (error) {
            console.error('İrsaliye silme hatası:', error);
            alert('İrsaliye silinirken bir hata oluştu: ' + error.message);
        } finally {
            setDeleting(false);
        }
    };

    const filteredShipments = useMemo(() => {
        if (!searchTerm) return shipments;
        const lower = searchTerm.toLowerCase();
        return shipments.filter(s =>
            s.customerName?.toLowerCase().includes(lower) ||
            s.orderNumber?.toString().includes(lower) ||
            s.cargoCompany?.toLowerCase().includes(lower) ||
            s.trackingNumber?.toLowerCase().includes(lower)
        );
    }, [shipments, searchTerm]);

    if (loading) return <div style={styles.container}>Yükleniyor...</div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>Tüm Sevk İrsaliyeleri</h2>
                <button
                    style={{ ...styles.actionBtn, background: '#06C167', color: 'white', border: 'none', padding: '10px 20px' }}
                    onClick={() => navigate('/siparisler/direkt-sevk-irsaliyesi')}
                >
                    + Yeni Direkt İrsaliye
                </button>
            </div>

            <div style={styles.box}>
                <div style={styles.searchInputContainer}>
                    <span style={styles.searchIcon}>🔍</span>
                    <input
                        type="text"
                        placeholder="Müşteri, sipariş no veya kargo bilgisi ile ara..."
                        style={{ ...styles.input, paddingLeft: '44px' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div style={styles.box}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Sipariş No</th>
                                <th style={styles.th}>Müşteri</th>
                                <th style={styles.th}>Ürün Sayısı</th>
                                <th style={styles.th}>Toplam Tutar</th>
                                <th style={styles.th}>Kargo Firması</th>
                                <th style={styles.th}>Takip No</th>
                                <th style={styles.th}>Durum</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredShipments.map((s) => {
                                const totalAmount = (s.items || []).reduce((acc, item) =>
                                    acc + (safeNumber(item.price) * safeNumber(item.quantity)), 0);
                                const currency = s.order?.currency === 'USD' ? '$' : '₺';
                                return (
                                    <tr
                                        key={s.id}
                                        style={styles.clickableRow}
                                        onClick={() => handleOpenDetail(s)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <td style={styles.td}>{s.shipmentDate?.seconds ? new Date(s.shipmentDate.seconds * 1000).toLocaleDateString('tr-TR') : '-'}</td>
                                        <td style={styles.td}><strong>#{s.orderNumber}</strong></td>
                                        <td style={styles.td}>{s.customerName}</td>
                                        <td style={styles.td}>{(s.items || []).length} kalem</td>
                                        <td style={styles.td}><strong>{totalAmount.toFixed(2)} {currency}</strong></td>
                                        <td style={styles.td}>{s.cargoCompany || '-'}</td>
                                        <td style={styles.td}>{s.trackingNumber || '-'}</td>
                                        <td style={styles.td}>
                                            <span style={{
                                                ...styles.statusBadge,
                                                background: s.status === 'Faturalandı' ? '#dcfce7' : '#f1f5f9',
                                                color: s.status === 'Faturalandı' ? '#166534' : '#64748b'
                                            }}>
                                                {s.status || 'Gönderildi'}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>
                                            <button
                                                style={styles.actionBtn}
                                                onClick={(e) => { e.stopPropagation(); generateShipmentPDF(s); }}
                                            >
                                                📄 PDF
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredShipments.length === 0 && (
                                <tr>
                                    <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                        {searchTerm ? 'Aramanızla eşleşen irsaliye bulunamadı.' : 'Henüz sevk irsaliyesi bulunmuyor.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail Modal */}
            {selectedShipment && (
                <div style={styles.modalOverlay} onClick={handleCloseModal}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div>
                                <h3 style={styles.modalTitle}>
                                    📋 Sevk İrsaliyesi Detayı
                                </h3>
                                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
                                    İrsaliye No: #{selectedShipment.id.substring(0, 8).toUpperCase()} &nbsp;|&nbsp; Sipariş No: #{selectedShipment.orderNumber}
                                </p>
                            </div>
                            <button style={styles.closeBtn} onClick={handleCloseModal}>✕</button>
                        </div>

                        <div style={styles.modalBody}>
                            {/* Info Cards */}
                            <div style={styles.infoGrid}>
                                <div style={styles.infoCard}>
                                    <div style={styles.infoLabel}>Müşteri</div>
                                    <div style={styles.infoValue}>{selectedShipment.customerName}</div>
                                    {selectedShipment.order?.customerPhone && (
                                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                                            📞 {selectedShipment.order.customerPhone}
                                        </div>
                                    )}
                                    {selectedShipment.order?.customerAddress && (
                                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                                            📍 {selectedShipment.order.customerAddress}
                                        </div>
                                    )}
                                </div>
                                <div style={styles.infoCard}>
                                    <div style={styles.infoLabel}>Sevk Tarihi</div>
                                    <div style={styles.infoValue}>
                                        {selectedShipment.shipmentDate?.seconds
                                            ? new Date(selectedShipment.shipmentDate.seconds * 1000).toLocaleDateString('tr-TR', {
                                                day: 'numeric', month: 'long', year: 'numeric'
                                            })
                                            : '-'}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                                        <span style={{
                                            ...styles.statusBadge,
                                            background: selectedShipment.status === 'Faturalandı' ? '#dcfce7' : '#f1f5f9',
                                            color: selectedShipment.status === 'Faturalandı' ? '#166534' : '#64748b'
                                        }}>
                                            {selectedShipment.status || 'Gönderildi'}
                                        </span>
                                    </div>
                                </div>
                                <div style={styles.infoCard}>
                                    <div style={styles.infoLabel}>Kargo Firması</div>
                                    {isEditing ? (
                                        <input
                                            style={styles.editInput}
                                            value={editedCargoCompany}
                                            onChange={e => setEditedCargoCompany(e.target.value)}
                                            placeholder="Kargo firması..."
                                        />
                                    ) : (
                                        <div style={styles.infoValue}>{selectedShipment.cargoCompany || '-'}</div>
                                    )}
                                </div>
                                <div style={styles.infoCard}>
                                    <div style={styles.infoLabel}>Takip Numarası</div>
                                    {isEditing ? (
                                        <input
                                            style={styles.editInput}
                                            value={editedTrackingNumber}
                                            onChange={e => setEditedTrackingNumber(e.target.value)}
                                            placeholder="Takip numarası..."
                                        />
                                    ) : (
                                        <div style={styles.infoValue}>{selectedShipment.trackingNumber || '-'}</div>
                                    )}
                                </div>
                            </div>

                            {/* VAT Input */}
                            <div style={{ ...styles.infoCard, marginTop: '12px', marginBottom: '24px' }}>
                                <div style={styles.infoLabel}>KDV Oranı (%)</div>
                                {isEditing ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            style={{ ...styles.editInput, width: '80px' }}
                                            value={Math.round(editedVatRate * 100)}
                                            onChange={e => setEditedVatRate(Number(e.target.value) / 100)}
                                        />
                                        <span style={{ fontSize: '14px', color: '#64748b' }}>%</span>
                                    </div>
                                ) : (
                                    <div style={styles.infoValue}>%{(editedVatRate * 100).toFixed(0)}</div>
                                )}
                            </div>

                            {/* Add Item Section */}
                            {isEditing && (
                                <div style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#f8fafc' }}>
                                    <h4 style={{ fontSize: '14px', marginBottom: '12px', color: '#0f172a' }}>➕ Ürün Ekle</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                                        <div style={{ position: 'relative' }}>
                                            <div style={styles.infoLabel}>Ürün Ara</div>
                                            <input
                                                style={styles.editInput}
                                                placeholder="Ürün adı veya barkod..."
                                                value={newItemSearch}
                                                onChange={e => { setNewItemSearch(e.target.value); setNewItemProductId(''); }}
                                            />
                                            {filteredNewItemProducts.length > 0 && (
                                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 10, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                                                    {filteredNewItemProducts.map(p => (
                                                        <div
                                                            key={p.id}
                                                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}
                                                            onClick={() => handleNewItemProductSelect(p)}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                                        >
                                                            <div style={{ fontWeight: '500', color: '#0f172a' }}>{p.name}</div>
                                                            <div style={{ color: '#64748b', fontSize: '12px' }}>Stok: {p.quantity} {p.unit} | Fiyat: {p.salePrice} ₺</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <div style={styles.infoLabel}>Miktar</div>
                                            <input
                                                type="number"
                                                min="1"
                                                style={styles.editInput}
                                                value={newItemQuantity}
                                                onChange={e => setNewItemQuantity(Number(e.target.value))}
                                            />
                                        </div>
                                        <div>
                                            <div style={styles.infoLabel}>Birim Fiyat</div>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                style={styles.editInput}
                                                value={newItemPrice}
                                                onChange={e => setNewItemPrice(Number(e.target.value))}
                                            />
                                        </div>
                                        <div>
                                            <div style={styles.infoLabel}>Toplam</div>
                                            <div style={{ padding: '10px', fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>
                                                {(newItemQuantity * newItemPrice).toFixed(2)}
                                            </div>
                                        </div>
                                        <button style={styles.btnPrimary} onClick={handleAddNewItem}>Ekle</button>
                                    </div>
                                </div>
                            )}

                            {/* Items Table */}
                            <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', marginBottom: '12px' }}>
                                📦 Ürünler
                            </h4>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Barkod</th>
                                            <th style={styles.th}>Ürün Adı</th>
                                            <th style={styles.th}>Miktar</th>
                                            <th style={styles.th}>Birim Fiyat</th>
                                            <th style={styles.th}>Toplam</th>
                                            {isEditing && <th style={styles.th} width="50">İşlem</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(isEditing ? editedItems : (selectedShipment.items || [])).map((item, index) => {
                                            const product = products.find(p => p.id === item.productId);
                                            const itemTotal = safeNumber(item.price) * safeNumber(item.quantity);
                                            const currency = selectedShipment.order?.currency === 'USD' ? '$' : '₺';
                                            return (
                                                <tr key={index}>
                                                    <td style={styles.td}>{product?.barcode || '-'}</td>
                                                    <td style={styles.td}><strong>{safeString(item.name)}</strong></td>
                                                    <td style={styles.td}>
                                                        {isEditing ? (
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                style={styles.editInputSmall}
                                                                value={item.quantity}
                                                                onChange={e => handleEditItemChange(index, 'quantity', e.target.value)}
                                                            />
                                                        ) : (
                                                            safeString(item.quantity)
                                                        )}
                                                    </td>
                                                    <td style={styles.td}>
                                                        {isEditing ? (
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                style={styles.editInputSmall}
                                                                value={item.price}
                                                                onChange={e => handleEditItemChange(index, 'price', e.target.value)}
                                                            />
                                                        ) : (
                                                            `${safeNumber(item.price).toFixed(2)} ${currency}`
                                                        )}
                                                    </td>
                                                    <td style={styles.td}>
                                                        <strong>{itemTotal.toFixed(2)} {currency}</strong>
                                                    </td>
                                                    {isEditing && (
                                                        <td style={styles.td}>
                                                            <button
                                                                onClick={() => handleRemoveEditItem(index)}
                                                                style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                                                                title="Ürünü Çıkar"
                                                            >
                                                                ✕
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Total */}
                            {/* Totals */}
                            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                {/* Calculations */}
                                {(() => {
                                    const itemsToCalc = isEditing ? editedItems : (selectedShipment.items || []);
                                    const subTotal = itemsToCalc.reduce((acc, item) => acc + (safeNumber(item.price) * safeNumber(item.quantity)), 0);
                                    const vatAmount = subTotal * editedVatRate;
                                    const grandTotal = subTotal + vatAmount;
                                    const currency = selectedShipment.order?.currency === 'USD' ? '$' : '₺';

                                    return (
                                        <>
                                            <div style={{ fontSize: '14px', color: '#64748b' }}>
                                                Ara Toplam: <strong>{subTotal.toFixed(2)} {currency}</strong>
                                            </div>
                                            <div style={{ fontSize: '14px', color: '#64748b' }}>
                                                KDV (%{(editedVatRate * 100).toFixed(0)}): <strong>{vatAmount.toFixed(2)} {currency}</strong>
                                            </div>
                                            <div style={styles.totalBox}>
                                                TOPLAM: {grandTotal.toFixed(2)} {currency}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    {isEditing ? (
                                        <>
                                            <button
                                                style={{ ...styles.btnSuccess, opacity: saving ? 0.6 : 1 }}
                                                onClick={handleSaveEdit}
                                                disabled={saving}
                                            >
                                                {saving ? '⏳ Kaydediliyor...' : '✓ Kaydet'}
                                            </button>
                                            <button
                                                style={styles.btnSecondary}
                                                onClick={handleCancelEdit}
                                                disabled={saving}
                                            >
                                                ✕ İptal
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button style={styles.btnPrimary} onClick={handleStartEdit}>
                                                ✏️ Düzenle
                                            </button>
                                            <button
                                                style={styles.btnSecondary}
                                                onClick={(e) => { e.stopPropagation(); generateShipmentPDF(selectedShipment); }}
                                            >
                                                📄 PDF İndir
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div>
                                    {!isEditing && (
                                        <button
                                            style={{ ...styles.btnDanger, opacity: deleting ? 0.6 : 1 }}
                                            onClick={handleDeleteShipment}
                                            disabled={deleting}
                                        >
                                            {deleting ? '⏳ Siliniyor...' : '🗑️ İrsaliyeyi Sil'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllShipments;
